/**
 * Redux slice for managing events state.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Event, EventsState, EventCreate } from '../types/event';
import eventService from '../services/eventService';

// Initial state
const initialState: EventsState = {
    events: [],
    currentEvent: null,
    total: 0,
    isLoading: false,
    error: null,
};

// Async thunk for fetching all events
export const fetchEvents = createAsyncThunk<
    { events: Event[]; total: number },
    void,
    { rejectValue: string }
>('events/fetchEvents', async (_, { rejectWithValue }) => {
    try {
        const response = await eventService.getEvents();
        return { events: response.events, total: response.total };
    } catch (error: unknown) {
        if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as { response?: { data?: { detail?: string } } };
            return rejectWithValue(
                axiosError.response?.data?.detail || 'Failed to fetch events'
            );
        }
        return rejectWithValue('Failed to fetch events');
    }
});

// Async thunk for fetching a single event
export const fetchEvent = createAsyncThunk<
    Event,
    string,
    { rejectValue: string }
>('events/fetchEvent', async (eventId, { rejectWithValue }) => {
    try {
        const event = await eventService.getEvent(eventId);
        return event;
    } catch (error: unknown) {
        if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as { response?: { data?: { detail?: string } } };
            return rejectWithValue(
                axiosError.response?.data?.detail || 'Failed to fetch event'
            );
        }
        return rejectWithValue('Failed to fetch event');
    }
});

// Async thunk for creating a new event
export const createEvent = createAsyncThunk<
    Event,
    EventCreate,
    { rejectValue: string }
>('events/createEvent', async (eventData, { rejectWithValue }) => {
    try {
        const event = await eventService.createEvent(eventData);
        return event;
    } catch (error: unknown) {
        if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as { response?: { data?: { detail?: string } } };
            return rejectWithValue(
                axiosError.response?.data?.detail || 'Failed to create event'
            );
        }
        return rejectWithValue('Failed to create event');
    }
});

// Async thunk for deleting an event
export const deleteEvent = createAsyncThunk<
    string,
    string,
    { rejectValue: string }
>('events/deleteEvent', async (eventId, { rejectWithValue }) => {
    try {
        await eventService.deleteEvent(eventId);
        return eventId;
    } catch (error: unknown) {
        if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as { response?: { data?: { detail?: string } } };
            return rejectWithValue(
                axiosError.response?.data?.detail || 'Failed to delete event'
            );
        }
        return rejectWithValue('Failed to delete event');
    }
});

// Events slice
export const eventsSlice = createSlice({
    name: 'events',
    initialState,
    reducers: {
        setCurrentEvent: (state, action: PayloadAction<Event | null>) => {
            state.currentEvent = action.payload;
            if (action.payload) {
                eventService.setLastEventId(action.payload.id);
            } else {
                eventService.clearLastEventId();
            }
        },
        clearCurrentEvent: (state) => {
            state.currentEvent = null;
            eventService.clearLastEventId();
        },
        clearError: (state) => {
            state.error = null;
        },
        clearEvents: (state) => {
            state.events = [];
            state.currentEvent = null;
            state.total = 0;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch events
        builder
            .addCase(fetchEvents.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchEvents.fulfilled, (state, action) => {
                state.isLoading = false;
                state.events = action.payload.events;
                state.total = action.payload.total;
                state.error = null;
            })
            .addCase(fetchEvents.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to fetch events';
            });

        // Fetch single event
        builder
            .addCase(fetchEvent.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchEvent.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentEvent = action.payload;
                state.error = null;
                // Also update in events list if present
                const index = state.events.findIndex(e => e.id === action.payload.id);
                if (index !== -1) {
                    state.events[index] = action.payload;
                }
            })
            .addCase(fetchEvent.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to fetch event';
            });

        // Create event
        builder
            .addCase(createEvent.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createEvent.fulfilled, (state, action) => {
                state.isLoading = false;
                state.events.unshift(action.payload);
                state.total += 1;
                state.error = null;
            })
            .addCase(createEvent.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to create event';
            });

        // Delete event
        builder
            .addCase(deleteEvent.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteEvent.fulfilled, (state, action) => {
                state.isLoading = false;
                state.events = state.events.filter(e => e.id !== action.payload);
                state.total -= 1;
                if (state.currentEvent?.id === action.payload) {
                    state.currentEvent = null;
                    eventService.clearLastEventId();
                }
                state.error = null;
            })
            .addCase(deleteEvent.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to delete event';
            });
    },
});

// Export actions
export const { setCurrentEvent, clearCurrentEvent, clearError, clearEvents } =
    eventsSlice.actions;

// Export reducer
export default eventsSlice.reducer;
